import {
  type AppError,
  InvalidTokenError,
  type ResultWithOauthToken,
} from '@packages/utils';
import {
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import type {MutationConfig} from '../../lib/react-query';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {
  ClipColumnResponse,
  ClipCountResponse,
  UsersMeClipsResponse,
} from '../entities/clip/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';
/**
 * Repositoriesの実態（implement）をここに書く
 */
export type Options = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

export type OptionsForHooks = {
  cookies?: Partial<Record<string, string>>;
  version?: string;
};

export type ColumnRepositoryProps = IUpdateColumnMyClipsBase & Options;
export type ReportsRepositoryProps = IUpdateReportsMyClipsBase & Options;

type IDeleteReportsMyClipsBase = {
  // https://github.com/howtv/gsskt_issues/issues/4372 の影響でstring型許容になる
  // TODO: 上記issueのリリース完了次第number型を外す
  reportId: number | string;
  reportType: string;
};
export type IDeleteReportsMyClips = IDeleteReportsMyClipsBase & Options;

type IDeleteColumnMyClipsBase = {
  // https://github.com/howtv/gsskt_issues/issues/4372 の影響でstring型許容になる
  // TODO: 上記issueのリリース完了次第number型を外す
  columnId: number | string;
};
export type IDeleteColumnMyClips = IDeleteColumnMyClipsBase & Options;

type IGetMyClipsOptionsBase = {
  limit: number;
  page: number;
  clipTypes: string;
};
type IGetMyClipsOptions = IGetMyClipsOptionsBase & Options;

type IUpdateColumnMyClipsBase = {
  // https://github.com/howtv/gsskt_issues/issues/4372 の影響でstring型許容になる
  // TODO: 上記issueのリリース完了次第number型を外す
  columnId: number | string;
};
export type IUpdateColumnMyClips = IUpdateColumnMyClipsBase & Options;

type IUpdateReportsMyClipsBase = {
  // https://github.com/howtv/gsskt_issues/issues/4372 の影響でstring型許容になる
  // TODO: 上記issueのリリース完了次第number型を外す
  reportId: number | string;
  reportType: string;
};
export type IUpdateReportsMyClips = IUpdateReportsMyClipsBase & Options;

type IUseDeleteColumnMyClipsOptions = {
  config?: MutationConfig<typeof deleteColumnMyClips>;
} & IDeleteColumnMyClipsBase &
  OptionsForHooks;
type IUseDeleteReportsMyClipsOptions = {
  config?: MutationConfig<typeof deleteReportsMyClips>;
} & IDeleteReportsMyClips;

type IUseGetMyClipsOptions = {
  config?: UseQueryOptions<
    ResultWithOauthToken<UsersMeClipsResponse, AppError>
  >;
  version?: string;
} & IGetMyClipsOptionsBase;
type IUseGetInfiniteMyClipsOptions = {
  config?: UseInfiniteQueryOptions<typeof getMyClips>;
  version?: string;
} & IGetMyClipsOptionsBase;

type IUseUpdateColumnMyClipsOptions = {
  config?: MutationConfig<typeof updateColumnMyClips>;
} & IUpdateColumnMyClips;
type IUseUpdateMyClipsOptions = {
  config?: MutationConfig<typeof updateReportsMyClips>;
} & IUpdateReportsMyClips;

export const deleteColumnMyClips = async ({
  cookies,
  columnId,
  version = DEFAULT_API_VERSION,
}: IDeleteColumnMyClips) => {
  const request = async (token: string) => {
    defaultClient.setToken(token);
    return defaultClient.delete(
      `/${version}/columns/${columnId}/clip`,
      ClipColumnResponse,
    );
  };

  const result = await sendApiRequest<ClipColumnResponse>({
    cookies,
    authRequired: true,
    request,
    version,
  });

  return result;
};

export const useDeleteColumnMyClips = ({
  columnId,
  version = DEFAULT_API_VERSION,
}: IUseDeleteColumnMyClipsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation({
    async mutationFn() {
      const cookies = parseCookies();

      const result = await deleteColumnMyClips({cookies, columnId, version});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export const deleteReportsMyClips = async ({
  cookies,
  reportType,
  reportId,
  version = DEFAULT_API_VERSION,
}: IUseDeleteReportsMyClipsOptions) => {
  const request = async (token: string) => {
    defaultClient.setToken(token);
    return defaultClient.delete(
      `/${version}/reports/${reportType}/${reportId}/clip`,
      ClipCountResponse,
    );
  };

  const result = await sendApiRequest<ClipCountResponse>({
    cookies,
    authRequired: true,
    request,
    version,
  });

  return result;
};

export const useDeleteReportsMyClips = ({
  reportType,
  reportId,
  version = DEFAULT_API_VERSION,
}: IUseDeleteReportsMyClipsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation({
    async mutationFn() {
      const cookies = parseCookies();

      const result = await deleteReportsMyClips({
        cookies,
        reportType,
        reportId,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export const getMyClips = async ({
  cookies,
  page,
  limit,
  clipTypes,
  version = DEFAULT_API_VERSION,
}: IGetMyClipsOptions) => {
  const request = async (token: string) => {
    defaultClient.setToken(token);
    const path = createPathWithQuery(`/${version}/users/me/clips`, {
      page,
      limit,
      types: clipTypes,
    });
    // TODO: clipがnullの場合があるので、一旦nullを許容するがbackend側で修正する必要あり
    return defaultClient.get(path, UsersMeClipsResponse);
  };

  const result = await sendApiRequest<UsersMeClipsResponse>({
    cookies,
    authRequired: true,
    request,
    version,
  });

  return result;
};

// MEMO: 以下は未使用だが今はおまじない的に残しておく
export const useMyClips = ({
  page,
  limit,
  clipTypes,
  version = DEFAULT_API_VERSION,
}: IUseGetMyClipsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery({
    queryKey: ['myClips', page, limit, clipTypes, version],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getMyClips({
        cookies,
        page,
        limit,
        clipTypes,
        version,
      });

      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export const useInfiniteMyClips = ({
  limit = 20,
  page,
  clipTypes,
  version = DEFAULT_API_VERSION,
}: IUseGetInfiniteMyClipsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useInfiniteQuery(
    [`myClips_${clipTypes}`],
    async () => {
      const cookies = parseCookies();
      const result = await getMyClips({
        cookies,
        page,
        limit,
        clipTypes,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage.ok && lastPage.value.nextPage > 0
          ? lastPage.value.nextPage
          : undefined,
    },
  );
};

export const updateColumnMyClips = async ({
  cookies,
  columnId,
  version = DEFAULT_API_VERSION,
}: IUpdateColumnMyClips) => {
  const request = async (token: string) => {
    defaultClient.setToken(token);
    return defaultClient.post(
      `/${version}/columns/${columnId}/clip`,
      ClipColumnResponse,
      {},
    );
  };

  const result = await sendApiRequest<ClipColumnResponse>({
    cookies,
    authRequired: true,
    request,
    version,
  });

  return result;
};

export const useUpdateColumnMyClips = ({
  columnId,
  version = DEFAULT_API_VERSION,
}: IUseUpdateColumnMyClipsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation<ResultWithOauthToken<ClipColumnResponse, AppError>>({
    async mutationFn() {
      const cookies = parseCookies();
      const result = await updateColumnMyClips({cookies, columnId, version});

      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export const updateReportsMyClips = async ({
  cookies,
  reportType,
  reportId,
  version = DEFAULT_API_VERSION,
}: IUpdateReportsMyClips) => {
  const request = async (token: string) => {
    defaultClient.setToken(token);
    return defaultClient.post(
      `/${version}/reports/${reportType}/${reportId}/clip`,
      ClipCountResponse,
      {},
    );
  };

  const result = await sendApiRequest<ClipCountResponse>({
    cookies,
    authRequired: true,
    request,
    version,
  });

  return result;
};

export const useUpdateReportsMyClips = ({
  reportType,
  reportId,
  version = DEFAULT_API_VERSION,
}: IUseUpdateMyClipsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation<ResultWithOauthToken<ClipCountResponse, AppError>>({
    async mutationFn() {
      const cookies = parseCookies();

      const result = await updateReportsMyClips({
        cookies,
        reportType,
        reportId,
        version,
      });

      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};
